import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Search, Eye, Edit2, Trash2, X, RefreshCw, Key, User,
  Mail, Phone, MessageSquare, Send, Archive, RotateCcw, Loader2, Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLIENT_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'business', label: 'Business' },
  { value: 'law_firm', label: 'Law Firm' },
  { value: 'bail_bond', label: 'Bail Bond Company' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'government', label: 'Government' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-green-500' },
  { value: 'inactive', label: 'Inactive', color: 'bg-gray-500' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-500' },
  { value: 'archived', label: 'Archived', color: 'bg-red-500' },
];

/**
 * Phase B — Clients LIST page. Create/Edit/View flows are now routed pages:
 *   /clients/new              → ClientFormPage (create)
 *   /clients/:clientId/edit   → ClientFormPage (edit)
 *   /clients/:clientId/view   → ClientViewPage
 * Only the "Generated Portal Credentials" reveal is kept as a small inline
 * dialog because it's a one-time secret display — not a full form.
 */
const Clients = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);

  // Iter121F — Reset Credentials dialog (write-through to the bridged
  // user, unlike the old resend-invite that silently orphaned the login).
  const [resetTarget, setResetTarget] = useState(null);
  const [resetUsername, setResetUsername] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetForce, setResetForce] = useState(true);
  const [resetting, setResetting] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/clients`, { headers, credentials: 'include' });
      if (res.ok) setClients(await res.json());
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const filteredClients = clients.filter((client) => {
    const name = client.name || `${client.first_name || ''} ${client.last_name || ''}`;
    const searchMatch = name.toLowerCase().includes(searchTerm.toLowerCase())
      || (client.primary_email || client.email || '').toLowerCase().includes(searchTerm.toLowerCase())
      || (client.business_name || client.company_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const typeMatch = filterType === 'all' || client.client_type === filterType;
    const statusMatch = filterStatus === 'all' || client.status === filterStatus;
    return searchMatch && typeMatch && statusMatch;
  });

  const handlePortalToggle = async (client, enabled) => {
    try {
      const res = await fetch(`${API_URL}/api/clients/${client.id}`, {
        method: 'PATCH', headers, credentials: 'include',
        body: JSON.stringify({ portal_enabled: enabled, portal_active: enabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(enabled ? 'Portal access enabled' : 'Portal access disabled');
        if (enabled && data.temp_password) {
          setGeneratedCredentials({
            username: data.portal_username || client.portal_username || client.primary_email || client.email,
            password: data.temp_password,
          });
          setShowCredentialsModal(true);
        }
        fetchClients();
      } else {
        toast.error(data.detail || 'Failed to update portal access');
      }
    } catch (err) {
      toast.error('Failed to update portal access');
    }
  };

  const handleResendInvite = async (client) => {
    try {
      const res = await fetch(`${API_URL}/api/clients/${client.id}/resend-invite`, {
        method: 'POST', headers, credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Invitation resent');
        setGeneratedCredentials({
          email: data.email,
          username: client.portal_username || data.email,
          password: data.temp_password,
        });
        setShowCredentialsModal(true);
      } else {
        toast.error(data.detail || 'Failed to resend invite');
      }
    } catch (err) {
      toast.error('Failed to resend invite');
    }
  };

  const handleArchive = async (client) => {
    if (!window.confirm('Are you sure you want to archive this client?')) return;
    try {
      const res = await fetch(`${API_URL}/api/clients/${client.id}/archive`, {
        method: 'POST', headers, credentials: 'include',
      });
      if (res.ok) { toast.success('Client archived'); fetchClients(); }
    } catch (err) { toast.error('Failed to archive client'); }
  };

  const handleRestore = async (client) => {
    try {
      const res = await fetch(`${API_URL}/api/clients/${client.id}/restore`, {
        method: 'POST', headers, credentials: 'include',
      });
      if (res.ok) { toast.success('Client restored'); fetchClients(); }
    } catch (err) { toast.error('Failed to restore client'); }
  };

  const handleDelete = async (client) => {
    const label = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim() || client.portal_username || client.id;
    if (!window.confirm(`Permanently delete client "${label}"?\n\nThis removes the client account and portal access. Active cases must be closed or reassigned first. This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/clients/${client.id}`, {
        method: 'DELETE', headers, credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { toast.success(`Client "${label}" deleted`); fetchClients(); }
      else toast.error(data.detail || 'Failed to delete client');
    } catch (err) { toast.error('Failed to delete client'); }
  };

  // Iter121F — open the Reset Credentials dialog for a specific client.
  const openResetDialog = (client) => {
    setResetTarget(client);
    setResetUsername(client.portal_username || client.primary_email || client.email || '');
    setResetPassword(''); // blank = auto-generate on server
    setResetForce(true);
  };

  const submitResetCredentials = async () => {
    if (!resetTarget) return;
    const uname = resetUsername.trim();
    if (uname.length < 4) {
      toast.error('Username must be at least 4 characters');
      return;
    }
    if (resetPassword && resetPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setResetting(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${resetTarget.id}/reset-credentials`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({
          username: uname,
          password: resetPassword || undefined,
          force_change_on_login: resetForce,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Failed to reset credentials');
        return;
      }
      toast.success('Credentials reset — the client can log in with these values now.');
      setGeneratedCredentials({ username: data.username, password: data.password });
      setResetTarget(null);
      setShowCredentialsModal(true);
      fetchClients();
    } catch (err) {
      toast.error('Failed to reset credentials');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Nexus Clients</h1>
          <p className="text-slate-400 text-sm">Command center for client accounts, portal access, credentials & case assignments</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchClients} variant="outline" size="sm" className="border-slate-600">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={() => navigate('/clients/new')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Client
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search clients..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white" />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white min-w-[140px]">
          <option value="all">All Types</option>
          {CLIENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white min-w-[120px]">
          <option value="all">All Status</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      <div className="bg-slate-800/60 rounded-lg border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto nx-inner-scroll-keep">
          <table className="w-full">
            <thead className="bg-slate-800 border-b border-slate-700">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Client</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Portal</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading clients…</td></tr>
              ) : filteredClients.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400">No clients found.</td></tr>
              ) : filteredClients.map((client) => {
                const name = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim() || '—';
                const type = CLIENT_TYPES.find((t) => t.value === client.client_type)?.label || 'Individual';
                const status = STATUS_OPTIONS.find((s) => s.value === client.status) || STATUS_OPTIONS[0];
                return (
                  <tr key={client.id} className="border-b border-slate-800 hover:bg-slate-800/40" data-testid={`client-row-${client.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-white font-medium truncate">{name}</p>
                          {(client.business_name || client.company_name) && (
                            <p className="text-xs text-slate-400 truncate">{client.business_name || client.company_name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">{type}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-slate-300 flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{client.primary_email || client.email || '—'}</span>
                        {(client.primary_phone || client.phone) && (
                          <span className="text-slate-400 text-xs flex items-center gap-1"><Phone className="w-3 h-3" />{client.primary_phone || client.phone}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => handlePortalToggle(client, !client.portal_enabled)}
                        className={`w-12 h-6 rounded-full transition-colors ${client.portal_enabled ? 'bg-green-500' : 'bg-slate-600'}`}
                        data-testid={`toggle-portal-${client.id}`}
                        title={client.portal_enabled ? 'Portal enabled — click to disable' : 'Portal disabled — click to enable'}
                      >
                        <span className={`block w-4 h-4 bg-white rounded-full transform transition-transform ${client.portal_enabled ? 'translate-x-7' : 'translate-x-1'}`} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${status.color} text-white`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end flex-wrap">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${client.id}/view`)} data-testid={`view-client-${client.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${client.id}/edit`)} data-testid={`edit-client-${client.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openResetDialog(client)}
                          title="Reset portal credentials"
                          className="text-amber-300 hover:text-amber-200"
                          data-testid={`reset-credentials-${client.id}`}
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        {client.portal_enabled && (
                          <Button variant="ghost" size="sm" onClick={() => handleResendInvite(client)} title="Resend invite" data-testid={`resend-invite-${client.id}`}>
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        {client.status === 'archived' ? (
                          <Button variant="ghost" size="sm" onClick={() => handleRestore(client)} title="Restore" data-testid={`restore-client-${client.id}`}>
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => handleArchive(client)} title="Archive" data-testid={`archive-client-${client.id}`}>
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300"
                          onClick={() => handleDelete(client)} title="Delete" data-testid={`delete-client-${client.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Iter121F — Reset Credentials dialog: writes to BOTH clients doc
          AND the bridged user record so the new username/password actually
          takes effect on the login page. */}
      {resetTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 sm:p-4" data-testid="reset-credentials-dialog">
          <div className="bg-slate-800 rounded-lg border border-slate-700 w-full max-w-md">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700">
              <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2 min-w-0">
                <Key className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="truncate">Reset Portal Credentials</span>
              </h3>
              <button onClick={() => setResetTarget(null)} className="text-slate-400 hover:text-white flex-shrink-0 ml-2" data-testid="reset-credentials-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <p className="text-sm text-slate-300">
                Rotating <span className="text-white font-semibold">{resetTarget.name || resetTarget.portal_username || 'this client'}</span>'s login. The new credentials take effect immediately on the NexusTrac login page.
              </p>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Portal Username</label>
                <Input
                  value={resetUsername}
                  onChange={(e) => setResetUsername(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="e.g. jakesmith or jake@company.com"
                  data-testid="reset-credentials-username"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">New Password <span className="text-slate-500">(leave blank to auto-generate a strong one)</span></label>
                <Input
                  type="text"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white font-mono"
                  placeholder="Auto-generate"
                  data-testid="reset-credentials-password"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetForce}
                  onChange={(e) => setResetForce(e.target.checked)}
                  className="rounded"
                  data-testid="reset-credentials-force"
                />
                Force the client to change password on next login
              </label>
              <div className="flex gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => setResetTarget(null)}
                  className="border-slate-600 !text-slate-200"
                  data-testid="reset-credentials-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitResetCredentials}
                  disabled={resetting}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="reset-credentials-submit"
                >
                  {resetting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rotating…</> : <><Key className="w-4 h-4 mr-2" /> Rotate Credentials</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generated Credentials — small confirm-style reveal kept intentionally
          (one-time secret display; not a form). */}
      {showCredentialsModal && generatedCredentials && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-slate-800 rounded-lg border border-slate-700 w-full max-w-md">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700">
              <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2 min-w-0">
                <Key className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span className="truncate">Client Portal Login Credentials</span>
              </h3>
              <button onClick={() => setShowCredentialsModal(false)} className="text-slate-400 hover:text-white flex-shrink-0 ml-2">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3 text-sm text-yellow-200">
                <strong>Important:</strong> Save these credentials now. The password cannot be retrieved later.
              </div>
              <div className="p-3 bg-slate-700/50 rounded">
                <p className="text-xs text-slate-400 mb-1">Username</p>
                <p className="text-white font-mono text-base break-all">{generatedCredentials.username}</p>
              </div>
              <div className="p-3 bg-slate-700/50 rounded">
                <p className="text-xs text-slate-400 mb-1">Password</p>
                <p className="text-white font-mono text-base break-all">{generatedCredentials.password}</p>
              </div>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(`Username: ${generatedCredentials.username}\nPassword: ${generatedCredentials.password}\nURL: /client/login`);
                  toast.success('Credentials copied to clipboard');
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Copy Credentials to Clipboard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
